import { LEVEL_LABELS, type ModuleEntry, type ModuleLevel } from '@/modules/architecture'

/**
 * One quiet line saying which Rev A modules the surface below realises
 * (AC-6.4). It is rendered ONCE, centrally, by `AppShell` — so all 32
 * pre-existing surfaces gained it with zero per-page edits.
 *
 * Renders NOTHING when the list is empty: a route with no declared
 * attribution says nothing rather than showing a placeholder or "unknown".
 *
 * Ink is `text-muted-foreground`, never `--color-faint` — this is data text.
 */
export function ModuleAttribution({ modules }: { modules: readonly ModuleEntry[] }) {
  if (modules.length === 0) return null

  const levels = modules.reduce<Partial<Record<ModuleLevel, ModuleEntry[]>>>((acc, module) => {
    const bucket = acc[module.level] ?? []
    bucket.push(module)
    acc[module.level] = bucket
    return acc
  }, {})

  return (
    <div
      data-testid="module-attribution"
      className="mb-4 flex flex-wrap items-center gap-x-2 gap-y-1 border-b border-hairline pb-3 text-xs text-muted-foreground"
    >
      <span className="text-[0.6875rem] font-semibold uppercase tracking-[0.1em]">Realises</span>
      {(Object.keys(levels) as ModuleLevel[]).map((level) => (
        <span key={level} className="flex flex-wrap items-center gap-x-2 gap-y-1">
          <span
            title={LEVEL_LABELS[level]}
            className="rounded-full bg-secondary px-1.5 py-0.5 text-[0.6875rem] font-semibold text-secondary-foreground"
          >
            {level}
          </span>
          <span>{(levels[level] ?? []).map((m) => m.name).join(' · ')}</span>
        </span>
      ))}
    </div>
  )
}
